import React from 'react';
import COLORS from "scss/colors.module.scss";
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  Typography,
} from "@material-ui/core";
import { components } from "react-select";
import { AiFillCaretDown } from "react-icons/ai";
import MultiLineReusableEditor from "./MultiLineReusableEditor";
import NumberReusableEditor from "./NumberReusableEditor";
import DateTimeReusableEditor from "./DateTimeReusableEditor";
import BooleanReusableEditor from "./BooleanReusableEditor";
import DropdownReusableEditor from "./DropdownReusableEditor";
import CheckboxGroupReusableEditor from "./CheckboxGroupReusableEditor";
import MultiSelectReusableEditor from "./MultiSelectReusableEditor";
import { UploaderPopup } from "../FileOrAttachment/UploaderPopup";
import LookupReusableEditor from "./LookupReusableEditor";
import SubsheetColumnEditor from "../Subsheet/SubsheetColumnEditor";
import CommentsPopover from "../Triggers/CommentsPopover";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

const editorTypes = {
    'text': ['1', '8', '14', '18', '20', '24', 1, 8, 14, 18, 20, 24],
    'number': ['5', '17', '22', 5, 17, 22],
    'date': [ '4', '16', 4, 16],
    'boolean': ['12', 12],
    'dropdown': ['3', 3],
    'checkbox': ['13', 13],
    'multiselect': ['11', 11],
    'file': ['9', 9],
    'lookup': ['10', 10, '10-1'],
    'subsheet': ['6', 6],
    'comments': ['comments_id']
};

const editorTypeLookup = {};
for (const [type, ids] of Object.entries(editorTypes)) {
  ids.forEach(id => editorTypeLookup[id] = type);
}

export const commonInputStyles = {
    width: "100%",
    fontSize: 13,
    border: `1px solid ${COLORS.MULTI_LINE_MODAL_BORDER}`,
    borderRadius: "6px",
    fontFamily: "Roboto, sans-serif",
    backgroundColor: COLORS.PRIMARY_COLOR,
    color: COLORS.TEXT_COLOR,
    padding: "12px",
};

export function ColumnName({columnName}) {
    return (
        <Typography variant="subtitle1" style={{ fontWeight: 600, color: COLORS.TEXT_COLOR, maxWidth: "100%", overflowX: 'auto' }}>
            {columnName}
        </Typography>
    );
}

export function SavingSpinnerForColumnEditors() {
    return (
        <Box
            position="absolute"
            top="0"
            left="0"
            width="100%"
            height="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            zIndex={10}
        >
            <CircularProgress size={32} />
        </Box>
    );
}

export function ActionButtonsForColumnEditors({handleSave, handleCancel, isSaveDisabled, isCloseDisabled}) {
    return (
        <DialogActions>
            <Button 
                onClick={handleCancel} 
                color="primary" 
                style={{
                    fontSize: '13px', 
                    cursor: 'pointer'
                }} 
                disabled={isCloseDisabled}
            >
                Close
            </Button>
            <Button 
                onClick={handleSave} 
                color="primary" 
                style={{
                    fontSize: '13px', 
                    cursor: 'pointer'
                }}
                disabled={isSaveDisabled}
            >
                Save
            </Button>
        </DialogActions>
    );
}

export function ErrorMessageViewer({errMsg}) {
    return (<Typography variant="caption" style={{ color: "red" }}>
        {errMsg}
    </Typography>);
}

export function DropdownIndicator(props) {
    return (
        <components.DropdownIndicator {...props}>
            <AiFillCaretDown size={16} style={{ color: "#555" }} />
        </components.DropdownIndicator>
    );
};

export async function showConfirmationPopup(titleValue, descriptionValue) {
    await window.appStore.notificationStore.confirm({
        title: <>{titleValue}</>,
        description: (
            <>
            {descriptionValue}
            </>
        ),
    });
}

export async function handleClose(value, initialValue, onCancel, validationError = {isInvalid: false, message: ''}, setValidationError) {
    if (onCancel) { //when clicked outside the popup this is needed.
        if (value !== initialValue && !validationError.isInvalid) {
            await showConfirmationPopup("Are you sure?", "The entered value won't be saved if you close.");
        }
        if (validationError.isInvalid && setValidationError) setValidationError({isInvalid: false, message: ''});
        onCancel();
    }
}

export function getEditorTypeByColumnTypeId(columnTypeId) {
    return editorTypeLookup[String(columnTypeId)] || '';
}

export function getColumnDefById(columnId) {
    return window?.appStore?.containerStore?.currentViewStore?.columnDefs?.find(colDef => colDef?.cellRendererParams?.columnId == columnId);
}

export function getLatestColumnInfo(column) {
    try {
        const latestColumnInfo = window?.appStore?.containerStore?.listConfig?.listData?.find(col => col.id === column?.id);
        if(latestColumnInfo) {
            return latestColumnInfo;
        }
        return column;
    } catch (error) {
        console.error("Error getting latest column info: ", error);
        return column;
    }
}

function onCancel(props) {
    props.setEditorOpen(false)
    if (props.store) props.store.isRowViewEditorOpen = false;
}

export function objectifyListValues(values) {
    return values.reduce((acc, val) => {
        acc[val] = 1;
        return acc;
    }, {});
};

export function checkValuesUpdation(initialValue, selectedValues) {
    const initVals = initialValue ? initialValue.split(",").map((v) => v.trim()) : [];
    if (initVals.length !== selectedValues.length) return true;
    const initialValues = objectifyListValues(initVals);
    let result = false;
    for (const val of selectedValues) {
        if (!initialValues[val]) {
            result = true;
            break;
        }
    }
    return result;
};

export function parseFiles(val) {
    try {
        if (Array.isArray(val)) return val;
        if (typeof val === "string") return JSON.parse(val || "[]");
        if (typeof val === "object" && val !== null) return [val];
        return [];
    } catch {
        return [];
    }
};

const handleFilesChange = async(updatedFiles, initialFiles, setLoading, onSave, column, bodyRef, store) => {
    try {
        store.rowDetailScrollPos = bodyRef?.current?.scrollTop ?? (store.rowDetailScrollPos ?? 0);
        setLoading(true);
        const initialFilesParsed = parseFiles(initialFiles);
        const updatedFilesParsed = parseFiles(updatedFiles);
        const allFilesParsed = [...initialFilesParsed, ...updatedFilesParsed];
        await onSave(allFilesParsed, initialFilesParsed, column);
    } catch (error) {
        console.error("Error handling files change: ", error);
        // setLoading(false);
    } finally {
        setLoading(false);
    }
};

export function getEditorByColumnType(props) {
    try {
        let columnType = getEditorTypeByColumnTypeId(props?.column?.column_type_id);
        let columnEditor = '';

        if (columnType) {
            switch (columnType) {
                case 'text':
                    columnEditor = <MultiLineReusableEditor
                        initialValue={props.editorValue}
                        open={props.editorOpen}
                        data={props.data}
                        columnName={props.editorColKey}
                        column={props.column}
                        onSave={(newValue, oldValue, column) => props.handleSaveValue(newValue, oldValue, column)}
                        onCancel={() => onCancel(props)}
                    />
                    break;

                case 'number':
                    columnEditor = <NumberReusableEditor
                        initialValue={props.editorValue}
                        open={props.editorOpen}
                        data={props.data}
                        column={props.column}
                        columnName={props.editorColKey}
                        onSave={(newValue, oldValue, column) => props.handleSaveValue(newValue, oldValue, column)}
                        onCancel={() => onCancel(props)}
                    />
                    break;

                case 'date':
                    columnEditor = <DateTimeReusableEditor
                        initialValue={props.editorValue}
                        open={props.editorOpen}
                        data={props.data}
                        column={props.column}
                        columnName={props.editorColKey}
                        onSave={(newValue, oldValue, column) => props.handleSaveValue(newValue, oldValue, column)}
                        onCancel={() => onCancel(props)}
                    />
                    break;

                case 'boolean':
                    columnEditor = <BooleanReusableEditor
                        initialValue={props.editorValue}
                        open={props.editorOpen}
                        data={props.data}
                        column={props.column}
                        columnName={props.editorColKey}
                        onSave={(newValue, oldValue, column) => props.handleSaveValue(newValue, oldValue, column)}
                        onCancel={() => onCancel(props)}
                    />
                    break;
                
                case 'dropdown':
                    columnEditor = <DropdownReusableEditor
                        initialValue={props.editorValue}
                        open={props.editorOpen}
                        data={props.data}
                        column={props.column}
                        columnName={props.editorColKey}
                        onSave={(newValue, oldValue, column) => props.handleSaveValue(newValue, oldValue, column)}
                        onCancel={() => onCancel(props)}
                    />
                    break;

                case 'checkbox':
                    columnEditor = <CheckboxGroupReusableEditor
                        initialValue={props.editorValue}
                        open={props.editorOpen}
                        data={props.data}
                        column={props.column}
                        columnName={props.editorColKey}
                        onSave={(newValue, oldValue, column) => props.handleSaveValue(newValue, oldValue, column)}
                        onCancel={() => onCancel(props)}
                    />
                    break;

                case 'multiselect':
                    columnEditor = <MultiSelectReusableEditor
                        initialValue={props.editorValue}
                        open={props.editorOpen}
                        data={props.data}
                        column={props.column}
                        columnName={props.editorColKey}
                        onSave={(newValue, oldValue, column) => props.handleSaveValue(newValue, oldValue, column)}
                        onCancel={() => onCancel(props)}
                    />
                    break;

                case 'file':
                    columnEditor = <UploaderPopup
                        onChange={(val)=> handleFilesChange(val, props.editorValue, props.setLoading, props.handleSaveValue, props.column, props.bodyRef, props.store)}
                        showModal={props.editorOpen}
                        setShowModal={props.setEditorOpen}
                        max_files={props.column?.normalizedColumnOptions?.max_files}
                        allowed_file_types={props.column?.normalizedColumnOptions?.allowed_file_types}
                        columnId={props?.column?.id}
                        fileCount={parseFiles(props.editorValue)?.length ?? 0}
                    />
                    break;
                
                case 'lookup':
                    columnEditor = <LookupReusableEditor
                        initialValue={props.editorValue}
                        open={props.editorOpen}
                        store={props.store}
                        data={props.data}
                        column={props.column}
                        columnName={props.editorColKey}
                        onSave={(newValue, oldValue, column) => props.handleSaveValue(newValue, oldValue, column)}
                        onCancel={() => onCancel(props)}
                    />
                    break;
                case 'subsheet':
                    const colDef = getColumnDefById(props.column.id);
                    const dataIncludingSubsheets = props?.dataIncludingSubsheets ?? null;
                    const editorKey = `${props.column.id}-${props?.rowNode?.data?.row_id}-${colDef?.cellRendererParams?.columnOptions?.sub_list_id}`;
                    columnEditor = (
                        <DndProvider backend={HTML5Backend}>
                            <SubsheetColumnEditor
                                key={editorKey}
                                value={props.editorValue}
                                open={props.editorOpen}
                                data={props.data}
                                listId={props.params?.listId}
                                colDef={colDef}
                                column={props.column}
                                node={props?.rowNode}
                                api={props.params?.api}
                                onSubsheetDataChange={(updatedData) => props.handleSubsheetUpdation(updatedData, getLatestColumnInfo(props.column))}
                                dataIncludingSubsheets={dataIncludingSubsheets}
                                setShowSubsheetEditor={props.setEditorOpen}
                                fromRowViewPanel={true}
                            />
                        </DndProvider>
                    );
                    break;

                case 'comments':
                    const anchorEl = document.body;
                    columnEditor = <CommentsPopover
                        open={props.editorOpen}
                        anchorEl={anchorEl}
                        setAnchorEl={()=>{}}
                        updateCount={(a,b)=>{}}
                        list_id={props.params?.listId}
                        row_id={props.data.row_id}
                        closePopper={props.handleSaveComments}
                        uniqueId={props.data.uniqueId ?? ''}
                        commentsList={props.data.comments || []}
                        fromRowViewPanel={true}
                    />
                    break;

                default:
                    break;
            }
        }

        return columnEditor;
    } catch (err) {
        console.log('Error fetching editor: ', err);
        props.setEditorOpen(false);
        if (props.store) props.store.isRowViewEditorOpen = false;
        return '';
    }
}